import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Paper
} from '@mui/material';
import {
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  Description as DescriptionIcon,
  Star as StarIcon
} from '@mui/icons-material';
import Logo from '../common/Logo';
import { validateBusinessEmail } from '../../utils/emailValidation';

interface PartnershipFormProps {
  open: boolean;
  onClose: () => void;
}

const tiers = [
  { value: 'tier1', label: 'Tier 1 - Premium Publishers', description: 'Major national/international publications with 1M+ monthly readers' },
  { value: 'tier2', label: 'Tier 2 - Established Publishers', description: 'Regional publications with 100K-1M monthly readers' },
  { value: 'tier3', label: 'Tier 3 - Growing Publishers', description: 'Local publications with 10K-100K monthly readers' },
  { value: 'tier4', label: 'Tier 4 - Emerging Publishers', description: 'New publications with 1K-10K monthly readers' }
];

const PartnershipForm: React.FC<PartnershipFormProps> = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    tier: '',
    description: '',
    monthlyReaders: '',
    geographicFocus: '',
    industryFocus: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string) => (event: any) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value
    });
    
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors({
        ...fieldErrors,
        [field]: ''
      });
    }
    
    // Validate email in real-time
    if (field === 'email' && value) {
      const emailValidation = validateBusinessEmail(value);
      if (!emailValidation.isValid) {
        setFieldErrors({
          ...fieldErrors,
          email: emailValidation.error || ''
        });
      } else {
        const newErrors = { ...fieldErrors };
        delete newErrors.email;
        setFieldErrors(newErrors);
      }
    }
    
    // Validate website URL format
    if (field === 'website' && value) {
      try {
        if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
          // Don't show error, just allow user to type
        } else if (value) {
          new URL(value);
          const newErrors = { ...fieldErrors };
          delete newErrors.website;
          setFieldErrors(newErrors);
        }
      } catch (e) {
        if (value.length > 0) {
          setFieldErrors({
            ...fieldErrors,
            website: 'Please enter a valid website URL (e.g., https://example.com)'
          });
        }
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setFieldErrors({});

    // Validate required fields
    const errors: Record<string, string> = {};
    
    if (!formData.companyName || formData.companyName.trim() === '') {
      errors.companyName = 'Company name is required';
    }
    
    if (!formData.contactName || formData.contactName.trim() === '') {
      errors.contactName = 'Contact name is required';
    }
    
    if (!formData.email || formData.email.trim() === '') {
      errors.email = 'Email address is required';
    } else {
      const emailValidation = validateBusinessEmail(formData.email);
      if (!emailValidation.isValid) {
        errors.email = emailValidation.error || 'Invalid email address';
      }
    }
    
    if (!formData.tier) {
      errors.tier = 'Please select a publishing tier';
    }
    
    // Validate website URL if provided
    if (formData.website && formData.website.trim() !== '') {
      try {
        let websiteUrl = formData.website.trim();
        if (!websiteUrl.startsWith('http://') && !websiteUrl.startsWith('https://')) {
          websiteUrl = 'https://' + websiteUrl;
        }
        new URL(websiteUrl);
      } catch (e) {
        errors.website = 'Please enter a valid website URL (e.g., https://example.com)';
      }
    }
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Please fix the errors in the form before submitting.');
      setLoading(false);
      return;
    }

    try {
      // Prepare website URL - add https:// if not present
      let websiteUrl = formData.website.trim();
      if (websiteUrl && !websiteUrl.startsWith('http://') && !websiteUrl.startsWith('https://')) {
        websiteUrl = 'https://' + websiteUrl;
      }
      
      const response = await fetch('/api/partnerships/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          website: websiteUrl || undefined,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Partnership application submitted successfully! We will contact you within 2 business days.');
        setFormData({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          website: '',
          tier: '',
          description: '',
          monthlyReaders: '',
          geographicFocus: '',
          industryFocus: ''
        });
        setTimeout(() => {
          onClose();
          setSuccess('');
        }, 3000);
      } else {
        setError(data.message || 'Failed to submit partnership application. Please try again.');
      }
    } catch (error) {
      console.error('Partnership submission error:', error);
      setError('Network error. Please check your connection and try again.');
    }

    setLoading(false);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '16px',
          padding: '8px'
        }
      }}
    >
      <DialogTitle style={{ textAlign: 'center', paddingBottom: '16px' }}>
        <Typography variant="h4" style={{ fontWeight: 'bold', color: '#0927eb', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          Partner with <Logo size="inline" animated={false} />
        </Typography>
        <Typography variant="body1" style={{ color: '#666' }}>
          Join our network of premium publishing channels
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" style={{ marginBottom: '16px' }}>{error}</Alert>}
        {success && <Alert severity="success" style={{ marginBottom: '16px' }}>{success}</Alert>}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Company Information */}
            <Grid item xs={12}>
              <Typography variant="h6" style={{ marginBottom: '16px', color: '#0927eb', fontWeight: 'bold' }}>
                Company Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Company Name *"
                value={formData.companyName}
                onChange={handleChange('companyName')}
                required
                error={!!fieldErrors.companyName}
                helperText={fieldErrors.companyName}
                InputProps={{
                  startAdornment: <BusinessIcon style={{ marginRight: '8px', color: '#666' }} />
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Website"
                value={formData.website}
                onChange={handleChange('website')}
                error={!!fieldErrors.website}
                helperText={fieldErrors.website || 'e.g., https://example.com'}
                InputProps={{
                  startAdornment: <LanguageIcon style={{ marginRight: '8px', color: '#666' }} />
                }}
              />
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Typography variant="h6" style={{ marginBottom: '16px', color: '#0927eb', fontWeight: 'bold', marginTop: '16px' }}>
                Contact Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Name *"
                value={formData.contactName}
                onChange={handleChange('contactName')}
                required
                error={!!fieldErrors.contactName}
                helperText={fieldErrors.contactName}
                InputProps={{
                  startAdornment: <BusinessIcon style={{ marginRight: '8px', color: '#666' }} />
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Address *"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                required
                error={!!fieldErrors.email}
                helperText={fieldErrors.email || 'Please use your business email address'}
                InputProps={{
                  startAdornment: <EmailIcon style={{ marginRight: '8px', color: '#666' }} />
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={handleChange('phone')}
                InputProps={{
                  startAdornment: <PhoneIcon style={{ marginRight: '8px', color: '#666' }} />
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Monthly Readers"
                value={formData.monthlyReaders}
                onChange={handleChange('monthlyReaders')}
                placeholder="e.g., 50,000"
              />
            </Grid>

            {/* Publishing Tier */}
            <Grid item xs={12}>
              <Typography variant="h6" style={{ marginBottom: '16px', color: '#0927eb', fontWeight: 'bold', marginTop: '16px' }}>
                Publishing Tier *
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required error={!!fieldErrors.tier}>
                <InputLabel>Select Your Publishing Tier</InputLabel>
                <Select
                  value={formData.tier}
                  onChange={handleChange('tier')}
                  label="Select Your Publishing Tier"
                >
                  {tiers.map((tier) => (
                    <MenuItem key={tier.value} value={tier.value}>
                      <div>
                        <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <StarIcon style={{ color: '#F36409' }} />
                          {tier.label}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '4px' }}>
                          {tier.description}
                        </div>
                      </div>
                    </MenuItem>
                  ))}
                </Select>
                {fieldErrors.tier && (
                  <Typography variant="caption" color="error" style={{ marginTop: '4px', marginLeft: '14px' }}>
                    {fieldErrors.tier}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Additional Information */}
            <Grid item xs={12}>
              <Typography variant="h6" style={{ marginBottom: '16px', color: '#0927eb', fontWeight: 'bold', marginTop: '16px' }}>
                Additional Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Geographic Focus"
                value={formData.geographicFocus}
                onChange={handleChange('geographicFocus')}
                placeholder="e.g., North America, Europe, Asia-Pacific"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Industry Focus"
                value={formData.industryFocus}
                onChange={handleChange('industryFocus')}
                placeholder="e.g., Technology, Healthcare, Finance"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Description"
                multiline
                rows={4}
                value={formData.description}
                onChange={handleChange('description')}
                placeholder="Tell us about your publication, audience, and why you'd like to partner with us..."
                InputProps={{
                  startAdornment: <DescriptionIcon style={{ marginRight: '8px', color: '#666', alignSelf: 'flex-start', marginTop: '8px' }} />
                }}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions style={{ padding: '24px', justifyContent: 'center' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          style={{
            borderColor: '#000000',
            color: '#000000',
            marginRight: '16px'
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          style={{
            backgroundColor: '#000000',
            color: 'white',
            fontWeight: 'bold'
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Partnership Application'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartnershipForm;
